<template>
  <div class="page-container">
    <div class="page-header">
      <h1 class="page-title">{{ pageTitle }}</h1>
      <button 
        class="btn btn-primary" 
        @click="handleCreate"
        :disabled="!hasPermission('program:create')"
      >
        <i class="ri-add-line"></i>
        新增程式
      </button>
    </div>

    <div class="page-toolbar">
      <div class="search-box">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋程式代碼或程式名稱..."
          @input="handleSearch"
        />
        <i class="ri-search-line"></i>
      </div>
      <div class="filter-group">
        <select
          v-model="selectedModuleId"
          class="form-control module-filter"
          @change="handleModuleFilter"
        >
          <option value="">全部模組</option>
          <option
            v-for="module in modules"
            :key="module.id"
            :value="module.id"
          >
            {{ module.moduleName }}
          </option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchPrograms">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>程式代碼</th>
            <th>程式名稱</th>
            <th>路徑</th>
            <th>詳細資訊</th>
            <th>排序</th>
            <th>狀態</th>
            <th>建立時間</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="programs.length === 0">
            <td colspan="8" class="empty-state">
              沒有找到程式
            </td>
          </tr>
          <tr v-for="program in programs" :key="program.id">
            <td>{{ program.programCode }}</td>
            <td>
              <div class="program-name-cell">
                <i v-if="program.icon" :class="program.icon" class="program-icon-display"></i>
                <i v-else class="ri-file-line program-icon-default"></i>
                <span>{{ program.programName }}</span>
              </div>
            </td>
            <td>{{ program.programPath || '-' }}</td>
            <td class="info-cell">
              <div class="info-icon-wrapper">
                <i class="ri-information-line info-icon"></i>
                <div class="info-tooltip">
                  <div class="tooltip-section">
                    <div class="tooltip-title">所屬模組</div>
                    <div v-if="program.modules && program.modules.length > 0" class="tooltip-content">
                      <span v-for="module in program.modules" :key="module.moduleId" class="tooltip-badge module-badge">
                        {{ module.moduleName }}
                      </span>
                    </div>
                    <div v-else class="tooltip-empty">未分配模組</div>
                  </div>
                  <div class="tooltip-section">
                    <div class="tooltip-title">可用操作</div>
                    <div v-if="program.permissions && program.permissions.length > 0" class="tooltip-content">
                      <span v-for="perm in getUniqueActions(program.permissions)" :key="perm" class="tooltip-badge action-badge">
                        {{ formatActionName(perm) }}
                      </span>
                    </div>
                    <div v-else class="tooltip-empty">無操作設定</div>
                  </div>
                </div>
              </div>
            </td>
            <td>{{ program.sortOrder }}</td>
            <td>
              <span
                :class="program.status === 'active' ? 'badge badge-success' : 'badge badge-danger'"
              >
                {{ program.status === 'active' ? '啟用' : '停用' }}
              </span>
            </td>
            <td>{{ formatDate(program.createdAt) }}</td>
            <td>
              <div class="action-buttons">
                <button
                  class="btn btn-sm btn-secondary"
                  @click="handleEdit(program)"
                  title="編輯"
                  :disabled="!hasPermission('program:update')"
                >
                  <i class="ri-edit-line"></i>
                </button>
                <button
                  class="btn btn-sm btn-danger"
                  @click="handleDelete(program)"
                  title="刪除"
                  :disabled="!hasPermission('program:delete')"
                >
                  <i class="ri-delete-bin-line"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ListPagination
      v-if="total > 0"
      :page="currentPage"
      :total-pages="totalPages"
      :total="total"
      @change="goToPage"
    />
  </div>
</template>

<script>
import { ref, onMounted, computed, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { usePermissions } from '@/composables/usePermissions.js';
import { programService } from '@/api/modules/programs.js';
import { moduleService } from '@/api/modules/modules.js';
import { showSuccess, showError, showDeleteConfirm } from '@/utils/swal.js';

export default {
  name: 'ProgramList',
  setup() {
    const router = useRouter();
    const route = useRoute();
    const { pageTitle } = usePageTitle('程式列表');
    const { hasPermission } = usePermissions();
    const programs = ref([]);
    const modules = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const searchQuery = ref('');
    const selectedModuleId = ref('');
    const currentPage = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    const searchTimeout = ref(null);

    const totalPages = computed(() => {
      return Math.ceil(total.value / pageSize.value);
    });

    const fetchModules = async () => {
      try {
        const response = await moduleService.getModules({ 
          page: 1, 
          pageSize: 100,
          status: 'active'
        });
        
        if (response && response.success && response.data) {
          if (response.data.items) {
            modules.value = response.data.items || [];
          } else if (Array.isArray(response.data)) {
            modules.value = response.data;
          }
        }
      } catch (err) {
        console.error('獲取模組列表失敗:', err);
      }
    };

    const fetchPrograms = async () => {
      loading.value = true;
      error.value = null;

      try {
        const params = {
          page: Number(currentPage.value),
          pageSize: Number(pageSize.value),
        };

        if (searchQuery.value && searchQuery.value.trim().length > 0) {
          params.search = searchQuery.value.trim();
        }

        // 只有當 selectedModuleId 有效時才加入參數
        if (selectedModuleId.value && selectedModuleId.value !== '' && selectedModuleId.value !== 'NaN') {
          params.moduleId = selectedModuleId.value;
        }

        const response = await programService.getPrograms(params);

        if (response && response.success && response.data) {
          if (response.data.items) {
            programs.value = response.data.items || [];
            total.value = response.data.pagination?.totalItems || response.data.pagination?.total || response.data.total || 0;
          } else if (Array.isArray(response.data)) {
            programs.value = response.data;
            total.value = response.data.length;
          }
        } else {
          error.value = response && response.message || '獲取程式列表失敗';
        }
      } catch (err) {
        console.error('獲取程式列表失敗:', err);
        error.value = err.message || '獲取程式列表失敗';
      } finally {
        loading.value = false;
      }
    };

    const handleModuleFilter = () => {
      currentPage.value = 1;
      fetchPrograms();
    };

    const handleSearch = () => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
      searchTimeout.value = setTimeout(() => {
        currentPage.value = 1;
        fetchPrograms();
      }, 500);
    };

    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        fetchPrograms();
      }
    };

    const handleCreate = () => {
      router.push('/system/programs/create');
    };

    const handleEdit = (program) => {
      const query = {};
      if (currentPage.value > 1) query.page = currentPage.value;
      if (searchQuery.value?.trim()) query.search = searchQuery.value.trim();
      if (selectedModuleId.value) query.moduleId = selectedModuleId.value;
      router.push({
        name: 'EditProgram',
        params: { id: program.id },
        query: Object.keys(query).length ? query : undefined
      });
    };

    const handleDelete = async (program) => {
      const confirmed = await showDeleteConfirm(`${program.programName} (${program.programCode})`);
      
      if (!confirmed) {
        return;
      }

      const originalLoading = loading.value;
      loading.value = true;
      error.value = null;

      try {
        const response = await programService.deleteProgram(program.id);

        if (response && response.success) {
          await showSuccess('刪除成功', `程式「${program.programName}」已成功刪除`);
          
          if (programs.value.length === 1 && currentPage.value > 1) {
            currentPage.value = currentPage.value - 1;
          }
          
          await fetchPrograms();
        } else {
          const errorMsg = response?.message || '刪除程式失敗';
          await showError('刪除失敗', errorMsg);
        }
      } catch (err) {
        console.error('刪除程式失敗:', err);
        let errorTitle = '刪除失敗';
        let errorText = '刪除程式失敗，請稍後再試';
        
        if (err.code === 'NOT_FOUND') {
          errorText = '程式不存在或已被刪除';
        } else if (err.code === 'FORBIDDEN') {
          errorText = '您沒有權限刪除此程式';
        } else if (err.response?.status === 404) {
          errorText = '程式不存在或路由錯誤';
        } else if (err.response?.status === 403) {
          errorText = '您沒有權限刪除此程式';
        } else if (err.message) {
          errorText = err.message;
        }
        
        await showError(errorTitle, errorText);
      } finally {
        loading.value = originalLoading;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('zh-TW');
    };

    const getStatusBadgeClass = (status) => {
      const statusMap = {
        'active': 'badge-success',
        'inactive': 'badge-secondary'
      };
      return statusMap[status] || 'badge-secondary';
    };

    const getStatusText = (status) => {
      const statusMap = {
        'active': '啟用',
        'inactive': '停用'
      };
      return statusMap[status] || status;
    };

    // 從權限代碼中提取唯一的操作類型
    const getUniqueActions = (permissions) => {
      if (!permissions || permissions.length === 0) return [];
      const actions = new Set();
      permissions.forEach(perm => {
        if (perm && perm.includes(':')) {
          const action = perm.split(':')[1];
          if (action) {
            actions.add(action);
          }
        }
      });
      return Array.from(actions).sort();
    };

    // 格式化操作名稱
    const formatActionName = (action) => {
      const actionMap = {
        'create': '新增',
        'read': '查詢',
        'update': '編輯',
        'delete': '刪除',
        'export': '匯出',
        'print': '列印'
      };
      return actionMap[action] || action;
    };

    onMounted(() => {
      if (route.query.page) currentPage.value = Math.max(1, parseInt(route.query.page, 10) || 1);
      if (route.query.search != null) searchQuery.value = route.query.search;
      if (route.query.moduleId != null) selectedModuleId.value = route.query.moduleId;
      fetchModules();
      fetchPrograms();
      
      // 添加工具提示框智能定位
      nextTick(() => {
        document.addEventListener('mouseover', (e) => {
          const icon = e.target.closest('.info-icon');
          if (!icon) return;
          
          const wrapper = icon.closest('.info-icon-wrapper');
          if (!wrapper) return;
          
          const tooltip = wrapper.querySelector('.info-tooltip');
          if (!tooltip) return;
          
          // 獲取圖標位置
          const iconRect = icon.getBoundingClientRect();
          
          // 計算工具提示框位置
          let top = iconRect.top;
          const left = iconRect.right + 15;
          
          // 獲取真實的 tooltip 高度（因為使用 opacity，元素始終在 DOM 中）
          const tooltipHeight = tooltip.offsetHeight;
          const windowHeight = window.innerHeight;
          
          // 檢查是否超出視窗底部
          if (top + tooltipHeight > windowHeight - 10) {
            top = windowHeight - tooltipHeight - 10;
          }
          
          // 檢查是否超出視窗頂部
          if (top < 10) {
            top = 10;
          }
          
          // 設置位置
          tooltip.style.top = top + 'px';
          tooltip.style.left = left + 'px';
          
          // 調整箭頭位置
          const arrowTop = iconRect.top - top + (iconRect.height / 2);
          tooltip.style.setProperty('--arrow-top', arrowTop + 'px');
        });
      });
    });

    return {
      programs,
      modules,
      loading,
      error,
      searchQuery,
      selectedModuleId,
      currentPage,
      totalPages,
      total,
      pageTitle,
      hasPermission,
      handleSearch,
      handleModuleFilter,
      goToPage,
      handleCreate,
      handleEdit,
      handleDelete,
      formatDate,
      getStatusBadgeClass,
      getStatusText,
      getUniqueActions,
      formatActionName,
      fetchPrograms,
    };
  },
};
</script>

<style scoped>
@import '~@/assets/scss/components/_table-common.scss';
@import '~@/assets/scss/components/_list-page-common.scss';

.filter-group {
  min-width: 200px;
}

.module-filter {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  font-size: 14px;
  cursor: pointer;
  transition: border-color 0.2s;
}

.module-filter:hover {
  border-color: #999;
}

.module-filter:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 3px rgba(0, 123, 255, 0.1);
}

.search-box {
  flex: 1;
  max-width: 400px;
}

.table-wrapper table {
  min-width: 1200px; /* 程式列表有 8 個欄位 */
}

/* 詳細資訊懸停顯示 */
.info-cell {
  text-align: center;
  position: relative;
}

/* 程式名稱圖標 */
.program-name-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.program-icon-display {
  font-size: 18px;
  color: #007bff;
  flex-shrink: 0;
}

.program-icon-default {
  font-size: 18px;
  color: #6c757d;
  flex-shrink: 0;
  opacity: 0.5;
}

.info-icon-wrapper {
  display: inline-block;
  position: relative;
}

.info-icon {
  font-size: 20px;
  color: #007bff;
  cursor: help;
  transition: all 0.2s;
}

.info-icon:hover {
  color: #0056b3;
  transform: scale(1.1);
}

.info-tooltip {
  position: fixed;
  left: calc(100% + 15px);
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 16px;
  min-width: 300px;
  max-width: 400px;
  max-height: 400px;
  overflow-y: auto;
  z-index: 1000;
  white-space: normal;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease-in;
}

/* 使用 JavaScript 動態定位 */
.info-icon-wrapper:hover .info-tooltip {
  opacity: 1;
  pointer-events: auto;
}

/* 工具提示箭頭（指向左側） */
.info-tooltip::before {
  content: '';
  position: absolute;
  right: 100%;
  top: var(--arrow-top, 20px);
  transform: translateY(-50%);
  border: 8px solid transparent;
  border-right-color: #dee2e6;
}

.info-tooltip::after {
  content: '';
  position: absolute;
  right: 100%;
  top: var(--arrow-top, 20px);
  transform: translateY(-50%);
  border: 7px solid transparent;
  border-right-color: white;
  margin-right: -1px;
}


.tooltip-section {
  margin-bottom: 16px;
}

.tooltip-section:last-child {
  margin-bottom: 0;
}

.tooltip-title {
  font-size: 12px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.tooltip-content {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.tooltip-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.tooltip-badge.module-badge {
  background: #48bb78;
  color: white;
}

.tooltip-badge.permission-badge {
  background: #667eea;
  color: white;
  font-family: monospace;
  font-size: 11px;
}

.tooltip-badge.action-badge {
  background: #667eea;
  color: white;
  font-size: 12px;
  font-weight: 500;
}

.tooltip-empty {
  color: #6c757d;
  font-size: 12px;
  font-style: italic;
}
</style>
